import {
  CAROUSEL_ANONYMOUS_ROUND_ORDER,
  CAROUSEL_MIN_IMPRESSIONS_FOR_RANKING,
  CAROUSEL_TARGET_FACILITY,
  CAROUSEL_TARGET_PATIENT,
  CAROUSEL_TARGET_PROFESSIONAL,
  normalizeCarouselTarget,
} from "./mapCarouselConstants";

/**
 * @param {{ impressions?: number, signups?: number }} stats
 * @param {number} minImpressions
 * @returns {{ tier: number, rate: number, index: number }}
 *   tier 0 = trusted signup rate, 1 = cold / low impressions, 2 = no slide id
 */
function rankKey(row, firestoreOrderIndex, statsBySlideId, minImpressions) {
  const id = typeof row?.id === "string" && row.id.trim() ? row.id.trim() : "";
  if (!id) {
    return { tier: 2, rate: 0, index: firestoreOrderIndex };
  }
  const st = statsBySlideId[id] || {};
  const imp = Number(st.impressions) || 0;
  const sup = Number(st.signups) || 0;
  if (imp < minImpressions) {
    return { tier: 1, rate: 0, index: firestoreOrderIndex };
  }
  return {
    tier: 0,
    rate: sup / Math.max(imp, 1),
    index: firestoreOrderIndex,
  };
}

function compareRanked(a, b, statsBySlideId, minImpressions) {
  const ka = rankKey(a.row, a.index, statsBySlideId, minImpressions);
  const kb = rankKey(b.row, b.index, statsBySlideId, minImpressions);
  if (ka.tier !== kb.tier) {
    return ka.tier - kb.tier;
  }
  if (ka.tier === 0 && ka.rate !== kb.rate) {
    return kb.rate - ka.rate;
  }
  return ka.index - kb.index;
}

/**
 * Sort enabled rows by signup rate (with cold-start tier), stable by Firestore order index.
 * @param {Array<{ row: object, index: number }>} indexedRows
 */
function sortIndexedRows(indexedRows, statsBySlideId, minImpressions) {
  return [...indexedRows].sort((a, b) =>
    compareRanked(a, b, statsBySlideId, minImpressions)
  );
}

/**
 * @param {object[]} rows enabled global slide rows (same target)
 * @param {Record<string, { impressions?: number, signups?: number }>} statsBySlideId
 * @param {number} [minImpressions]
 * @returns {object[]}
 */
export function orderRowsForSegment(rows, statsBySlideId, minImpressions) {
  const minImp =
    minImpressions ?? CAROUSEL_MIN_IMPRESSIONS_FOR_RANKING;
  const indexed = rows.map((row, index) => ({ row, index }));
  return sortIndexedRows(indexed, statsBySlideId, minImp).map((x) => x.row);
}

/**
 * Patient → Provider → Facility rounds: best-ranked per target, then second-best each, etc.
 * @param {object[]} rows all enabled global rows
 * @param {Record<string, { impressions?: number, signups?: number }>} statsBySlideId
 * @param {number} [minImpressions]
 * @returns {object[]}
 */
export function orderRowsForAnonymousTriTarget(rows, statsBySlideId, minImpressions) {
  const minImp =
    minImpressions ?? CAROUSEL_MIN_IMPRESSIONS_FOR_RANKING;
  const buckets = {
    [CAROUSEL_TARGET_PATIENT]: [],
    [CAROUSEL_TARGET_PROFESSIONAL]: [],
    [CAROUSEL_TARGET_FACILITY]: [],
  };
  rows.forEach((row, index) => {
    const t = normalizeCarouselTarget(row.target);
    if (buckets[t]) {
      buckets[t].push({ row, index });
    } else {
      buckets[CAROUSEL_TARGET_PATIENT].push({ row, index });
    }
  });

  const queues = CAROUSEL_ANONYMOUS_ROUND_ORDER.map((target) =>
    sortIndexedRows(buckets[target] || [], statsBySlideId, minImp)
  );

  const out = [];
  let progress = true;
  while (progress) {
    progress = false;
    for (let i = 0; i < queues.length; i++) {
      const q = queues[i];
      if (q.length > 0) {
        out.push(q.shift().row);
        progress = true;
      }
    }
  }
  return out;
}

/**
 * @param {{
 *   anonymous: boolean,
 *   statsBySlideId: Record<string, { impressions?: number, signups?: number }>,
 *   statsReady?: boolean,
 *   minImpressions?: number,
 * }} options
 * @param {object[]} segmentRows rows for signed-in path (already filtered by segment)
 * @param {object[]} allEnabledRows rows for anonymous path (all targets)
 * @returns {object[]}
 */
export function orderGlobalRowsForMapCarousel(
  segmentRows,
  allEnabledRows,
  {
    anonymous,
    statsBySlideId,
    statsReady = true,
    minImpressions,
  }
) {
  if (!statsReady || !statsBySlideId || typeof statsBySlideId !== "object") {
    return anonymous ? [...allEnabledRows] : [...segmentRows];
  }

  const minImp =
    minImpressions ?? CAROUSEL_MIN_IMPRESSIONS_FOR_RANKING;

  if (anonymous) {
    return orderRowsForAnonymousTriTarget(
      allEnabledRows,
      statsBySlideId,
      minImp
    );
  }
  return orderRowsForSegment(segmentRows, statsBySlideId, minImp);
}
